import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from '@almacenes/ui';
import logo from '@/assets/logos/logo.svg';
import { useAuthStore, useSession, flowRepository, cuponPendienteStorage } from '@almacenes/core';
import { useQueryClient } from '@tanstack/react-query';
import { FaCheckCircle, FaCreditCard } from 'react-icons/fa';

type Phase = 1 | 2 | 3;

/**
 * Página que muestra el proceso de registro de tarjeta y activación automática de suscripción premium.
 * Se muestra cuando el usuario completa el proceso de registro de tarjeta con processPayment=true.
 * 
 * Flujo en 3 Fases:
 * 1. Fase 1: Tarjeta registrada exitosamente (solo visual, confirmado al llegar)
 * 2. Fase 2: Crear suscripción premium con la tarjeta registrada (POST /subcription/plan/creditcard)
 * 3. Fase 3: Polling para verificar el pago y activación (GET /v2/flow/status)
 */
export const RegistroTarjetaSuscripcionPremiumPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session: cachedSession, logout } = useAuthStore();
  const { data: session, isLoading: isLoadingSession } = useSession();
  const [searchParams] = useSearchParams();
  
  // Estados
  const [currentPhase, setCurrentPhase] = useState<Phase>(1);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [phase2Error, setPhase2Error] = useState<string | null>(null);
  const [phase3Error, setPhase3Error] = useState<string | null>(null);
  const [isPaid, setIsPaid] = useState(false);
  
  // Referencias para evitar duplicados
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollingRef = useRef(false);
  const phase2ExecutedRef = useRef(false);

  // Obtener RUT de query params o de sesión
  const rutParam = searchParams.get('rut');
  const effectiveSession = session || cachedSession;
  const effectiveRut = rutParam || effectiveSession?.almacen?.rut;
  
  // Obtener planId desde variable de entorno para versión premium
  const planId = parseInt(import.meta.env.VITE_FLOW_PLAN_ID_VERSION_ALMACEN_PREMIUM || '20', 10);

  // Fase 1: Automáticamente avanzar a Fase 2 después de 2 segundos
  useEffect(() => {
    if (currentPhase === 1) {
      const timer = setTimeout(() => {
        setCurrentPhase(2);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [currentPhase]);

  // Fase 2: Crear suscripción premium con tarjeta
  useEffect(() => {
    if (currentPhase !== 2 || phase2ExecutedRef.current) {
      return;
    }

    // Verificar que tengamos todos los datos necesarios
    if (!effectiveRut || !effectiveSession?.user?.email || !effectiveSession?.user?.nombre) {
      console.log('[RegistroTarjetaSuscripcionPremiumPage] Fase 2: Esperando datos de sesión...', {
        effectiveRut,
        email: effectiveSession?.user?.email,
        nombre: effectiveSession?.user?.nombre,
        isLoadingSession
      });
      return;
    }

    // Marcar como ejecutado SOLO después de verificar que tenemos los datos
    phase2ExecutedRef.current = true;

    const createSubscription = async () => {
      console.log('[RegistroTarjetaSuscripcionPremiumPage] Fase 2: Creando suscripción premium con tarjeta');

      // Recuperar el cupón aplicado antes de salir a Flow (si existe)
      const cuponPendiente = cuponPendienteStorage.obtener();

      try {
        // Convertir RUT de "12345678-9" a número 12345678
        const rutNumerico = parseInt(effectiveRut.split('-')[0], 10);

        console.log('[RegistroTarjetaSuscripcionPremiumPage] Llamando a createSubscriptionWithCreditCard con:', {
          rutAlmacen: rutNumerico,
          planId: planId,
          customerName: effectiveSession.user.nombre,
          customerEmail: effectiveSession.user.email,
          cuponId: cuponPendiente?.cuponId,
        });

        const response = await flowRepository.createSubscriptionWithCreditCard({
          rutAlmacen: rutNumerico,
          planId: planId,
          customerName: effectiveSession.user.nombre,
          customerEmail: effectiveSession.user.email,
          ...(cuponPendiente && { cuponId: cuponPendiente.cuponId }),
        });

        console.log('[RegistroTarjetaSuscripcionPremiumPage] Respuesta Fase 2:', response);

        if (response.status === 200) {
          // Suscripción creada exitosamente, avanzar a Fase 3
          console.log('[RegistroTarjetaSuscripcionPremiumPage] Suscripción premium creada exitosamente, avanzando a Fase 3');
          setCurrentPhase(3);
        } else {
          // Error en la creación (status 400 u otro)
          console.error('[RegistroTarjetaSuscripcionPremiumPage] Error en creación de suscripción, status:', response.status);
          setPhase2Error(response.message || 'Error al crear la suscripción premium');
        }
      } catch (err: any) {
        console.error('[RegistroTarjetaSuscripcionPremiumPage] Error en Fase 2:', err);
        console.error('[RegistroTarjetaSuscripcionPremiumPage] Detalles del error:', err?.response?.data);
        setPhase2Error(err?.response?.data?.message || 'Error al comunicarse con el servidor');
      } finally {
        // El cupón pendiente ya fue consumido en este intento (éxito o error)
        cuponPendienteStorage.limpiar();
      }
    };

    createSubscription();
  }, [currentPhase, effectiveRut, effectiveSession?.user?.email, effectiveSession?.user?.nombre, planId, isLoadingSession]);

  // Fase 3: Polling para verificar pago
  useEffect(() => {
    if (currentPhase !== 3 || !effectiveRut || isPollingRef.current) {
      return;
    }

    console.log('[RegistroTarjetaSuscripcionPremiumPage] Fase 3: Iniciando polling para verificar pago premium');
    isPollingRef.current = true;

    // Iniciar polling del estado de suscripción
    const checkSubscriptionStatus = async () => {
      try {
        console.log('[RegistroTarjetaSuscripcionPremiumPage] Consultando estado de suscripción premium...');
        
        // Llamar al endpoint de estado (el RUT ya viene con formato "12345678-9")
        const statusResponse = await flowRepository.getSubscriptionStatus(effectiveRut);
        
        console.log('[RegistroTarjetaSuscripcionPremiumPage] Estado recibido:', statusResponse);

        // Verificar si la suscripción está activa y pagada
        if (statusResponse.status === 'ACCEPTED') {
          console.log('[RegistroTarjetaSuscripcionPremiumPage] ¡Suscripción premium confirmada como pagada!');
          setIsPaid(true);

          // Detener el polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          // Detener el contador de tiempo
          if (timeIntervalRef.current) {
            clearInterval(timeIntervalRef.current);
            timeIntervalRef.current = null;
          }

          // Invalidar la sesión para obtener los permisos actualizados
          console.log('[RegistroTarjetaSuscripcionPremiumPage] Invalidando sesión...');
          await queryClient.invalidateQueries({ queryKey: ['session'] });

          // Esperar un momento antes de redirigir
          setTimeout(() => {
            console.log('[RegistroTarjetaSuscripcionPremiumPage] Redirigiendo al dashboard...');
            navigate('/dashboard');
          }, 2000);
        }
      } catch (error: any) {
        console.error('[RegistroTarjetaSuscripcionPremiumPage] Error al consultar estado:', error);
        
        // Si es un error 404, significa que aún no hay suscripción, seguir esperando
        if (error.response?.status === 404) {
          console.log('[RegistroTarjetaSuscripcionPremiumPage] Suscripción aún no disponible (404), continuando polling...');
        } else {
          // Otros errores, mostrar mensaje
          setPhase3Error('Ocurrió un error al verificar el estado de tu suscripción premium. Intenta recargar la página.');
        }
      }
    };

    // Ejecutar primera consulta inmediatamente
    checkSubscriptionStatus();

    // Iniciar polling cada 5 segundos
    pollingIntervalRef.current = setInterval(() => {
      checkSubscriptionStatus();
    }, 5000);

    // Iniciar contador de tiempo transcurrido (actualizar cada segundo)
    timeIntervalRef.current = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    // Timeout de 30 minutos
    const timeoutId = setTimeout(() => {
      console.log('[RegistroTarjetaSuscripcionPremiumPage] Timeout de 30 minutos alcanzado');
      setPhase3Error('El proceso de confirmación está tomando más tiempo de lo esperado. Por favor, contacta con soporte.');
      
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
        timeIntervalRef.current = null;
      }
    }, 30 * 60 * 1000); // 30 minutos

    // Cleanup al desmontar
    return () => {
      console.log('[RegistroTarjetaSuscripcionPremiumPage] Desmontando componente, limpiando intervalos...');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
      clearTimeout(timeoutId);
      isPollingRef.current = false;
    };
  }, [currentPhase, effectiveRut, navigate, queryClient]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleVolverSuscripcion = () => {
    navigate('/suscripciones/premium');
  };

  // Formatear tiempo transcurrido (mm:ss)
  const formatElapsedTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F3F4F9' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
        <img 
          src={logo} 
          alt="Almacenes Digitales" 
          className="h-9"
        />
        <Button
          variant="danger"
          onClick={handleLogout}
          size="sm"
        >
          Cerrar sesión
        </Button>
      </div>

      {/* Contenido */}
      <div className="flex-1 flex items-center justify-center px-8 py-4">
        <div className="max-w-4xl w-full">
          <div className="bg-white rounded-3xl shadow-xl p-12">
            {isPaid ? (
              // Estado de éxito
              <>
                <div className="text-center mb-8">
                  <div className="text-8xl mb-6">🎉</div>
                  <h1 className="text-4xl font-bold mb-4" style={{ color: '#11194F' }}>
                    ¡Suscripción Premium Activada!
                  </h1>
                  <p className="text-gray-600 text-lg">
                    Tu suscripción premium ha sido activada exitosamente. Redirigiendo al dashboard...
                  </p>
                </div>

                <div className="flex justify-center">
                  <Spinner size="lg" />
                </div>
              </>
            ) : (
              // Estado de procesamiento (con o sin errores)
              <>
                <div className="text-center mb-12">
                  <h1 className="text-4xl font-bold mb-6" style={{ color: '#11194F' }}>
                    {phase2Error || phase3Error ? 'Error al Procesar Suscripción' : 'Procesando tu Suscripción Premium'}
                  </h1>
                  <p className="text-gray-600 text-lg mb-2">
                    {phase2Error || phase3Error
                      ? 'Ocurrió un error durante el proceso. Revisa los detalles a continuación.'
                      : 'Estamos confirmando el pago y activando tu cuenta premium.'}
                  </p>
                  {!phase2Error && !phase3Error && (
                    <p className="text-gray-500 text-sm">
                      Este proceso puede tardar algunos momentos...
                    </p>
                  )}
                </div>

                {/* Fases del proceso */}
                <div className="space-y-6 mb-12">
                  {/* Fase 1: Tarjeta registrada */}
                  <div className={`flex items-start gap-4 rounded-xl p-6 ${
                    currentPhase >= 1
                      ? 'bg-green-50 border-2 border-green-200'
                      : 'bg-gray-50 border-2 border-gray-200'
                  }`}>
                    {currentPhase >= 1 ? (
                      <FaCheckCircle className="text-3xl text-green-500 flex-shrink-0 mt-1" />
                    ) : (
                      <FaCreditCard className="text-3xl text-gray-400 flex-shrink-0 mt-1" />
                    )}
                    <div>
                      <h3 className={`text-lg font-semibold mb-1 ${
                        currentPhase >= 1 ? 'text-green-800' : 'text-gray-500'
                      }`}>
                        Fase 1: Tarjeta Registrada
                      </h3>
                      <p className={`text-sm ${
                        currentPhase >= 1 ? 'text-green-700' : 'text-gray-500'
                      }`}>
                        Tu tarjeta de crédito ha sido registrada exitosamente en nuestro sistema.
                      </p>
                    </div>
                  </div>

                  {/* Fase 2: Crear suscripción */}
                  <div className={`flex items-start gap-4 rounded-xl p-6 ${
                    phase2Error
                      ? 'bg-red-50 border-2 border-red-300'
                      : currentPhase > 2
                      ? 'bg-green-50 border-2 border-green-200'
                      : currentPhase === 2
                      ? 'bg-blue-50 border-2 border-blue-200'
                      : 'bg-gray-50 border-2 border-gray-200'
                  }`}>
                    {phase2Error ? (
                      <span className="text-3xl text-red-500 flex-shrink-0 mt-1">❌</span>
                    ) : currentPhase > 2 ? (
                      <FaCheckCircle className="text-3xl text-green-500 flex-shrink-0 mt-1" />
                    ) : currentPhase === 2 ? (
                      <Spinner size="md" className="flex-shrink-0 mt-1" />
                    ) : (
                      <span className="text-3xl text-gray-400 flex-shrink-0 mt-1">2️⃣</span>
                    )}
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold mb-1 ${
                        phase2Error
                          ? 'text-red-800'
                          : currentPhase > 2
                          ? 'text-green-800'
                          : currentPhase === 2
                          ? 'text-blue-800'
                          : 'text-gray-500'
                      }`}>
                        Fase 2: Creando Suscripción Premium
                      </h3>
                      <p className={`text-sm ${
                        phase2Error
                          ? 'text-red-700'
                          : currentPhase > 2
                          ? 'text-green-700'
                          : currentPhase === 2
                          ? 'text-blue-700'
                          : 'text-gray-500'
                      }`}>
                        {phase2Error
                          ? phase2Error
                          : currentPhase > 2
                          ? 'Suscripción premium creada exitosamente.'
                          : currentPhase === 2
                          ? 'Estamos creando tu suscripción premium con la tarjeta registrada...'
                          : 'Pendiente de crear suscripción premium.'}
                      </p>
                    </div>
                  </div>

                  {/* Fase 3: Procesando pago */}
                  <div className={`flex items-start gap-4 rounded-xl p-6 ${
                    phase3Error
                      ? 'bg-red-50 border-2 border-red-300'
                      : currentPhase === 3
                      ? 'bg-blue-50 border-2 border-blue-200'
                      : 'bg-gray-50 border-2 border-gray-200'
                  }`}>
                    {phase3Error ? (
                      <span className="text-3xl text-red-500 flex-shrink-0 mt-1">❌</span>
                    ) : currentPhase === 3 ? (
                      <Spinner size="md" className="flex-shrink-0 mt-1" />
                    ) : (
                      <span className="text-3xl text-gray-400 flex-shrink-0 mt-1">3️⃣</span>
                    )}
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold mb-1 ${
                        phase3Error
                          ? 'text-red-800'
                          : currentPhase === 3
                          ? 'text-blue-800'
                          : 'text-gray-500'
                      }`}>
                        Fase 3: Confirmando Pago
                      </h3>
                      <p className={`text-sm ${
                        phase3Error
                          ? 'text-red-700'
                          : currentPhase === 3
                          ? 'text-blue-700'
                          : 'text-gray-500'
                      }`}>
                        {phase3Error
                          ? phase3Error
                          : currentPhase === 3
                          ? 'Verificando y confirmando el pago con Flow...'
                          : 'Pendiente de confirmar pago.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Información adicional - Solo mostrar durante polling (Fase 3) */}
                {currentPhase === 3 && !phase3Error && (
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">⏱️</span>
                      <div>
                        <p className="text-amber-800 font-semibold mb-1">
                          Tiempo transcurrido: {formatElapsedTime(elapsedTime)}
                        </p>
                        <p className="text-amber-700 text-sm">
                          Por favor, no cierres esta ventana. La confirmación del pago puede tardar unos momentos.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botón de volver cuando hay error */}
                {(phase2Error || phase3Error) && (
                  <Button
                    variant="primary"
                    onClick={handleVolverSuscripcion}
                    className="w-full py-4 text-lg font-semibold mb-6"
                    size="lg"
                  >
                    Volver a Suscripción Premium
                  </Button>
                )}

                {/* Mensaje de ayuda */}
                {!phase2Error && !phase3Error && (
                  <div className="text-center">
                    <p className="text-gray-500 text-sm">
                      {currentPhase === 3
                        ? 'Si el proceso tarda más de 5 minutos, por favor contacta con nuestro equipo de soporte.'
                        : 'Procesando tu solicitud...'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
